import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Shared components
import { ErrorComponent } from '../Shared/ErrorComponent/ErrorComponent';
import { Loading } from '../Shared/Loading/Loading';

// Tab components
import { WeeklyLogOverviewTab } from './WeeklyLogOverviewTab';
import { WeeklyLogGenderTab } from './WeeklyLogGenderTab';
import { WeeklyLogAgeTab } from './WeeklyLogAgeTab';
import { WeeklyLogTimezoneTab } from './WeeklyLogTimezoneTab';

import { IWeeklyLogAnalyticsResponse } from '../../common/interfaces/WeeklyLogAnalyticsResponse.interface';

interface WeeklyLogAnalyticsDashboardProps {
  analyticsData: IWeeklyLogAnalyticsResponse;
  isLoading: boolean;
  error: unknown;
}

export const WeeklyLogAnalyticsDashboard: React.FC<
  WeeklyLogAnalyticsDashboardProps
> = ({ analyticsData, isLoading, error }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <ErrorComponent errorMessage={t('common.error.loadingData')} />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('analytics.tabs.overview') },
    { id: 'gender', label: t('analytics.tabs.gender') },
    { id: 'age', label: t('analytics.tabs.age') },
    { id: 'timezone', label: t('analytics.tabs.timezone') },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <WeeklyLogOverviewTab analyticsData={analyticsData} />;
      case 'gender':
        return <WeeklyLogGenderTab analyticsData={analyticsData} />;
      case 'age':
        return <WeeklyLogAgeTab analyticsData={analyticsData} />;
      case 'timezone':
        return <WeeklyLogTimezoneTab analyticsData={analyticsData} />;
      default:
        return <WeeklyLogOverviewTab analyticsData={analyticsData} />;
    }
  };

  return (
    <div className="p-6">
      {/* Tabs */}
      <div className="mb-6 flex space-x-1 rounded-lg bg-gray-100 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-700 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {renderTabContent()}
    </div>
  );
};
